import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { ErrorObject } from 'ajv';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import type { SymbolPickerAction } from 'src/features/symbol-picker/state/actions';
import type {
    OrderData,
    BaseSchema,
    MarketData,
    PriceTicker,
    Leg,
} from 'src/routes/widgets/TradeProWidget/state/types';

export type StartFetchingExecutorSchemaAction = {
    type: 'START_FETCHING_EXECUTOR_SCHEMA';
};
export const startFetchingExecutorSchema = (): StartFetchingExecutorSchemaAction => ({
    type: 'START_FETCHING_EXECUTOR_SCHEMA',
});

export type FetchedExecutorSchemaAction = {
    type: 'FETCHED_EXECUTOR_SCHEMA';
    schema: BaseSchema;
    accountId?: string;
};
export const fetchedExecutorSchema = (schema: BaseSchema, accountId?: string): FetchedExecutorSchemaAction => ({
    type: 'FETCHED_EXECUTOR_SCHEMA',
    schema,
    accountId,
});

export type FailedFetchingExecutorSchemaAction = {
    type: 'FAILED_FETCHING_EXECUTOR_SCHEMA';
    error: Error;
};
export const failedFetchingExecutorSchema = (error: Error): FailedFetchingExecutorSchemaAction => ({
    type: 'FAILED_FETCHING_EXECUTOR_SCHEMA',
    error,
});

export type FallbackToBasicSchemaAction = {
    type: 'FALLBACK_TO_BASIC_SCHEMA';
};
export const fallbackToBasicSchema = (): FallbackToBasicSchemaAction => ({
    type: 'FALLBACK_TO_BASIC_SCHEMA',
});

export type SetExDestinationAndRouteFromBaseSchemaDefaultsAction = {
    type: 'SET_EXDESTINATION_AND_ROUTE_FROM_BASE_SCHEMA_DEFAULTS';
};
export const setExDestinationAndRouteFromBaseSchemaDefaults =
    (): SetExDestinationAndRouteFromBaseSchemaDefaultsAction => ({
        type: 'SET_EXDESTINATION_AND_ROUTE_FROM_BASE_SCHEMA_DEFAULTS',
    });

export type ResetOrderDataAction = {
    type: 'RESET_ORDER_DATA';
    orderData: Partial<OrderData> | null;
};
export const resetOrderData = (orderData?: Partial<OrderData> | null): ResetOrderDataAction => ({
    type: 'RESET_ORDER_DATA',
    orderData: orderData ?? null,
});

export type SetShowTradeToastAction = {
    type: 'SET_SHOW_TRADE_TOAST';
    value: boolean;
};
export const setShowTradeToast = (value: boolean): SetShowTradeToastAction => ({
    type: 'SET_SHOW_TRADE_TOAST',
    value,
});

export type SetSkipTradeConfirmationAction = {
    type: 'SET_SKIP_TRADE_CONFIRMATION';
    value: boolean;
};
export const setSkipTradeConfirmation = (value: boolean): SetSkipTradeConfirmationAction => ({
    type: 'SET_SKIP_TRADE_CONFIRMATION',
    value,
});

export type SetInstrumentAction = {
    type: 'SET_INSTRUMENT';
    value: Instrument;
    quantity?: number;
};
export const setInstrument = (value: Instrument, quantity?: number): SetInstrumentAction => ({
    type: 'SET_INSTRUMENT',
    value,
    quantity,
});

export type SetRouteAction = {
    type: 'SET_ROUTE';
    value: string;
};
export const setRoute = (value: string): SetRouteAction => ({
    type: 'SET_ROUTE',
    value,
});

export type SetPriceAction = {
    type: 'SET_PRICE';
    value: string;
};
export const setPrice = (value: string): SetPriceAction => ({
    type: 'SET_PRICE',
    value,
});

export type SetStopPriceAction = {
    type: 'SET_STOP_PRICE';
    value: string;
};
export const setStopPrice = (value: string): SetStopPriceAction => ({
    type: 'SET_STOP_PRICE',
    value,
});

export type SetStopLimitPriceAction = {
    type: 'SET_STOP_LIMIT_PRICE';
    stop: string;
    limit: string;
};
export const setStopLimitPrice = (stop: string, limit: string): SetStopLimitPriceAction => ({
    type: 'SET_STOP_LIMIT_PRICE',
    stop,
    limit,
});

export type PatchOrderDataAction = {
    type: 'PATCH_ORDER_DATA';
    value: Partial<OrderData>;
};
export const patchOrderData = (value: Partial<OrderData>): PatchOrderDataAction => ({
    type: 'PATCH_ORDER_DATA',
    value,
});

export type SetOrderErrorsAction = {
    type: 'SET_ORDER_ERRORS';
    value: ErrorObject[];
};
export const setOrderErrors = (value: ErrorObject[]): SetOrderErrorsAction => ({
    type: 'SET_ORDER_ERRORS',
    value,
});

export type SetPriceByClickingATickerAction = {
    type: 'SET_PRICE_BY_CLICKING_A_TICKER';
    value: number | undefined;
    ticker: PriceTicker;
};
export const setPriceByClickingATicker = (
    value: number | undefined,
    ticker: PriceTicker,
): SetPriceByClickingATickerAction => ({
    type: 'SET_PRICE_BY_CLICKING_A_TICKER',
    value,
    ticker,
});

export type SetMarketDataAction = {
    type: 'SET_MARKET_DATA';
    payload: Partial<MarketData>;
};
export const setMarketData = (payload: Partial<MarketData>): SetMarketDataAction => ({
    type: 'SET_MARKET_DATA',
    payload,
});

export type SetSelectedAccountEffectiveAccessAction = {
    type: 'SET_SELECTED_ACCOUNT_EFFECTIVE_ACCESS';
    permission: PermissionType;
};
export const setSelectedAccountEffectiveAccess = (
    permission: PermissionType,
): SetSelectedAccountEffectiveAccessAction => ({
    type: 'SET_SELECTED_ACCOUNT_EFFECTIVE_ACCESS',
    permission,
});

// Initialize state action (determines which type)
export type InitializeStateAction = {
    type: 'INITIALIZE_STATE';
    payload: {
        stateType: 'single-leg' | 'multi-leg';
        showTradeToast: boolean;
        skipTradeConfirmation: boolean;
        instrument: Instrument;
        account: string;
    };
};
export const initializeState = (payload: InitializeStateAction['payload']): InitializeStateAction => ({
    type: 'INITIALIZE_STATE',
    payload,
});

// Multi-leg specific actions
export type AddLegAction = {
    type: 'ADD_LEG';
    leg: Leg;
    legType: InstrumentType;
};
export const addLeg = (leg: Leg, legType: InstrumentType): AddLegAction => ({
    type: 'ADD_LEG',
    leg,
    legType,
});

export type UpdateLegAction = {
    type: 'UPDATE_LEG';
    legId: string;
    updates: Partial<Leg>;
};
export const updateLeg = (legId: string, updates: Partial<Leg>): UpdateLegAction => ({
    type: 'UPDATE_LEG',
    legId,
    updates,
});

export type RemoveLegAction = {
    type: 'REMOVE_LEG';
    legId: string;
};
export const removeLeg = (legId: string): RemoveLegAction => ({
    type: 'REMOVE_LEG',
    legId,
});

export type SetActiveStrategyAction = {
    type: 'SET_ACTIVE_STRATEGY';
    strategy: string;
};
export const setActiveStrategy = (strategy: string): SetActiveStrategyAction => ({
    type: 'SET_ACTIVE_STRATEGY',
    strategy,
});

export type SetLegsAction = {
    type: 'SET_LEGS';
    legs: Leg[];
};
export const setLegs = (legs: Leg[]): SetLegsAction => ({
    type: 'SET_LEGS',
    legs,
});

// Action to dispatch symbol picker actions to a specific leg
export type UpdateLegSymbolPickerAction = {
    type: 'UPDATE_LEG_SYMBOL_PICKER';
    legId: string;
    symbolPickerAction: SymbolPickerAction;
};
export const updateLegSymbolPicker = (
    legId: string,
    symbolPickerAction: SymbolPickerAction,
): UpdateLegSymbolPickerAction => ({
    type: 'UPDATE_LEG_SYMBOL_PICKER',
    legId,
    symbolPickerAction,
});

// Union type of all actions
export type TradeProAction =
    | StartFetchingExecutorSchemaAction
    | FetchedExecutorSchemaAction
    | FailedFetchingExecutorSchemaAction
    | FallbackToBasicSchemaAction
    | SetExDestinationAndRouteFromBaseSchemaDefaultsAction
    | ResetOrderDataAction
    | SetShowTradeToastAction
    | SetSkipTradeConfirmationAction
    | SetInstrumentAction
    | SetRouteAction
    | SetPriceAction
    | SetStopPriceAction
    | SetStopLimitPriceAction
    | PatchOrderDataAction
    | SetOrderErrorsAction
    | SetPriceByClickingATickerAction
    | SetMarketDataAction
    | SetSelectedAccountEffectiveAccessAction
    | InitializeStateAction
    | AddLegAction
    | UpdateLegAction
    | RemoveLegAction
    | SetActiveStrategyAction
    | SetLegsAction
    | UpdateLegSymbolPickerAction;
